import { QueryClientProvider } from '@tanstack/react-query'
import { useEffect } from 'react'
import { DndProvider } from 'react-dnd'
import { HTML5Backend } from 'react-dnd-html5-backend'
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom'
import { ProtectedRoute } from './components/auth'
import { useDragDropConfig } from './hooks/useDragDropConfig'
import { queryClient } from './lib/queryClient'
import { useAuthStore } from './store/auth.store'

// Auth pages (no lazy)
import {
  AuthLayout,
  EmailVerification,
  LoginForm,
  RegisterForm,
} from './pages/Auth'

// Main pages (no lazy)
import { ForgotPasswordForm } from './pages/Auth/ForgotPasswordForm'
import { ResetPasswordForm } from './pages/Auth/ResetPasswordForm'
import { BoardView } from './pages/Board'
import { CalendarPage } from './pages/Calendar/Calendar'
import { MyTasks } from './pages/MyTasks/MyTasks'
import { NotFoundPage } from './pages/NotFound'
import Profile from './pages/Profile/Profile'
import ProfileResetPassword from './pages/Profile/ProfileResetPassword'
import { WorkspaceBoardsPage, WorkspacesPage } from './pages/Workspaces'

/**
 * PublicRoute Component
 */
const PublicRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, isLoading } = useAuthStore()

  if (isLoading) {
    return <>{children}</>
  }

  if (isAuthenticated) {
    return <Navigate to="/workspaces" replace />
  }

  return <>{children}</>
}

/**
 * Main App Component
 */
function App() {
  const { loadUser } = useAuthStore()

  // Configure drag & drop
  useDragDropConfig()

  useEffect(() => {
    loadUser()
  }, [loadUser])

  return (
    <QueryClientProvider client={queryClient}>
      <DndProvider backend={HTML5Backend}>
        <BrowserRouter>
          <Routes>
            {/* Public routes */}
            <Route
              path="/login"
              element={
                <PublicRoute>
                  <AuthLayout>
                    <LoginForm />
                  </AuthLayout>
                </PublicRoute>
              }
            />
            <Route
              path="/register"
              element={
                <PublicRoute>
                  <AuthLayout>
                    <RegisterForm />
                  </AuthLayout>
                </PublicRoute>
              }
            />
            <Route
              path="/verify-email"
              element={
                <PublicRoute>
                  <AuthLayout>
                    <EmailVerification />
                  </AuthLayout>
                </PublicRoute>
              }
            />
            {/* // AuthLayout bilan: */}
            <Route
              path="/forgot-password"
              element={
                <AuthLayout>
                  <ForgotPasswordForm />
                </AuthLayout>
              }
            />
            <Route
              path="/reset-password"
              element={
                <AuthLayout>
                  <ResetPasswordForm />
                </AuthLayout>
              }
            />
            {/* Protected routes */}
            <Route
              path="/workspaces"
              element={
                <ProtectedRoute>
                  <WorkspacesPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/profile"
              element={
                <ProtectedRoute>
                  <Profile />
                </ProtectedRoute>
              }
            />
            <Route
              path="/profile/reset-password"
              element={
                <ProtectedRoute>
                  <ProfileResetPassword />
                </ProtectedRoute>
              }
            />
            <Route
              path="/calendar"
              element={
                <ProtectedRoute>
                  <CalendarPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/my-tasks"
              element={
                <ProtectedRoute>
                  <MyTasks />
                </ProtectedRoute>
              }
            />
            <Route
              path="/workspaces/:workspaceId"
              element={
                <ProtectedRoute>
                  <WorkspaceBoardsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/boards/:boardId"
              element={
                <ProtectedRoute>
                  <BoardView />
                </ProtectedRoute>
              }
            />
            <Route
              path="/boards/:boardId/cards/:cardId"
              element={
                <ProtectedRoute>
                  <BoardView />
                </ProtectedRoute>
              }
            />
            {/* Default */}
            <Route path="/" element={<Navigate to="/workspaces" replace />} />
            {/* 404 */}
            <Route path="*" element={<NotFoundPage />} />
          </Routes>
        </BrowserRouter>
      </DndProvider>
    </QueryClientProvider>
  )
}

export default App
